"use client"
import { useState, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Shield, Users, Zap, ArrowRight, CheckCircle, TrendingUp, type LucideIcon } from "lucide-react"
import { ThemeToggle } from "@/components/theme-toggle"
import {
  BUTTON,
  BUTTON_ACCENT,
  ACCENT_TEXT,
  ACCENT_BG,
  GRAY_BG,
  SECTION_STYLES,
  CONTAINER_STYLES,
  HEADING_STYLES,
  SUBHEADING_STYLES,
  ICON,
  CARD,
  CARD_HOVER,
} from "@/components/styles/basic"
import { Logo } from "@/components/layout/Logo"
import { useRouter } from "next/navigation"
import { Footer } from "@/components/layout/Footer"
// Types
interface FeatureItem {
  icon: LucideIcon
  title: string
  description: string
}
// Data
const challenges: FeatureItem[] = [
  {
    icon: Users,
    title: "60% Retiring",
    description: "60% of the current nuclear workforce will retire within 15 years."
  },
  {
    icon: TrendingUp,
    title: "$80+ Billion",
    description: "In nuclear projects planned in Ontario alone."
  },
  {
    icon: Shield,
    title: "Knowledge Loss",
    description: "Risk of losing decades of specialized nuclear knowledge."
  }
]
const solutions: FeatureItem[] = [
  {
    icon: CheckCircle,
    title: "Labor Multiplier Effect",
    description: "AI agents perform routine tasks with minimal human oversight, reducing effort by 50-80%"
  },
  {
    icon: Shield,
    title: "Knowledge Preservation",
    description: "Capture and codify expertise from retiring workforce for future generations"
  },
  {
    icon: Zap,
    title: "Regulatory Compliance",
    description: "Built-in compliance with CNSC, NRC, IAEA, and ASME standards"
  }
]
const Landing = () => {
  const router = useRouter()
  const [email, setEmail] = useState("")
  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
  }, [])
  return (
    <div className="min-h-screen">
      {/* Navigation Header */}
      <header className="backdrop-blur">
        <div className={`${CONTAINER_STYLES} flex h-16 items-center justify-between`}>
          <Logo />
          <div className="flex items-center gap-4">
            <ThemeToggle />
            <Button className={BUTTON} onClick={() => { router.push('/login') }}>Login</Button>
            <Button className={BUTTON_ACCENT} onClick={() => { router.push('/') }}>
              Request Demo
            </Button>
          </div>
        </div>
      </header>
      {/* Hero Section */}
      <section className={`relative overflow-hidden ${ACCENT_BG}`}>
        <div className={`${CONTAINER_STYLES} relative py-20`}>
          <div className="mx-auto max-w-4xl text-center">
            <h1 className={`mb-6 font-heading text-4xl font-extrabold leading-tight md:text-5xl ${ACCENT_TEXT}`}>
              Transform Nuclear Projects and Operations with <span>AI Catalysts</span>
            </h1>
            <p className="mx-auto mb-8 max-w-2xl text-xl leading-relaxed text-gray-500 dark:text-gray-400">
              Lower costs. Move faster. Preserve knowledge.
            </p>
            {/* Email Signup */}
            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              <div className="flex w-full sm:w-auto">
                <Input
                  type="email"
                  placeholder="Enter your work email"
                  value={email}
                  onChange={handleEmailChange}
                  className="flex-1 sm:w-80"
                />
                <Button className={`ml-2 ${BUTTON_ACCENT}`}>
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              Join leading nuclear organizations already using NPXai Workforce
            </p>
          </div>
        </div>
      </section>
      {/* Problem Statement */}
      <section className={SECTION_STYLES + " " + GRAY_BG}>
        <div className={CONTAINER_STYLES}>
          <div className="mb-16 text-center">
            <h2 className={HEADING_STYLES}>The Nuclear Workforce Challenge</h2>
            <p className={SUBHEADING_STYLES}>
              The nuclear industry faces an unprecedented workforce crisis that threatens critical infrastructure projects
            </p>
          </div>
          <div className="mx-auto grid max-w-5xl gap-8 md:grid-cols-3">
            {challenges.map((challenge) => (
              <Card key={challenge.title} className={CARD + " " + CARD_HOVER}>
                <CardHeader className="pb-4 text-center">
                  <div className={"mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full " + ICON}>
                    <challenge.icon className="h-8 w-8" />
                  </div>
                  <CardTitle className={`font-heading text-2xl ${ACCENT_TEXT}`}>{challenge.title}</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-gray-500 dark:text-gray-400">{challenge.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      {/* Solution Section */}
      <section className={SECTION_STYLES}>
        <div className={CONTAINER_STYLES}>
          <div className="mb-16 text-center">
            <h2 className={HEADING_STYLES}>The NPXai Workforce Solution</h2>
            <p className={SUBHEADING_STYLES}>
              Specialized AI Agents (Catalysts) that integrate seamlessly into your organization&apos;s workflows.
            </p>
          </div>
          <div className="mx-auto max-w-4xl">
            <div className="space-y-6">
              {solutions.map((solution) => (
                <div key={solution.title} className="flex items-start gap-4">
                  <div className={"flex h-12 w-12 shrink-0 items-center justify-center rounded-lg " + ICON}>
                    <solution.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className={`mb-2 font-heading text-xl font-bold ${ACCENT_TEXT}`}>{solution.title}</h3>
                    <p className="text-gray-500 dark:text-gray-400">{solution.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className={SECTION_STYLES + " " + GRAY_BG}>
        <div className={`${CONTAINER_STYLES} text-center`}>
          <h2 className={HEADING_STYLES}>Ready to Transform Your Nuclear Operations?</h2>
          <p className={`${SUBHEADING_STYLES} mb-8`}>
            Join the future of nuclear energy with AI-powered workforce solutions
          </p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <Button size="lg" className={BUTTON_ACCENT}>
              Request Demo
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button size="lg" className={BUTTON} onClick={() => { router.push('/login') }}>Sign In</Button>
          </div>
        </div>
      </section>
      {/* Footer */}
      <Footer />
    </div>
  )
}

export default Landing